import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Shield, CheckCircle, Loader,
    Users, Zap, Link, Truck, MessageSquare
} from 'lucide-react';
import { useToastContext } from '../../contexts/ToastContext';
import { adminAPI } from '../../utils/api';
import { anonymizeName } from '../../utils/demoMode.js';
import './IntegrationSettings.scss';

const IntegrationSettings = () => {
    const { showSuccess, showError } = useToastContext();

    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [reviewSettingsLoading, setReviewSettingsLoading] = useState(false);
    const [reviewSettingsSaving, setReviewSettingsSaving] = useState(false);
    const [reviewSettings, setReviewSettings] = useState({
        enabled: true,
        testModeOnly: true,
        fieldroutesOnly: true,
        channel: 'sms',
        initialDelayHours: 0,
        platform: 'google'
    });
    const [campaignStats, setCampaignStats] = useState({
        statusBreakdown: [],
        responseRate: '0%'
    });
    const [testAppointmentsLoading, setTestAppointmentsLoading] = useState(false);
    const [testAppointments, setTestAppointments] = useState([]);
    const [selectedTestAppointmentId, setSelectedTestAppointmentId] = useState('');
    const [sendingTestSolicitation, setSendingTestSolicitation] = useState(false);

    // Selective sync state
    const [showSelectiveSync, setShowSelectiveSync] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [fieldroutesUsers, setFieldroutesUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [filterType, setFilterType] = useState('all');
    const [syncOptions, setSyncOptions] = useState({
        matchWithGHL: true
    });

    const [pestpacSaving, setPestpacSaving] = useState(false);
    const [pestpacTesting, setPestpacTesting] = useState(false);
    const [pestpacConfigured, setPestpacConfigured] = useState(false);
    const [pestpacCredentials, setPestpacCredentials] = useState({
        clientId: '',
        clientSecret: '',
        tenantId: '',
        apiKey: '',
        username: '',
        password: ''
    });
    const [providerWebhooksSaving, setProviderWebhooksSaving] = useState(false);
    const [providerWebhooks, setProviderWebhooks] = useState({
        pestpacTenantId: '',
        pestpacWebhookSecret: '',
        pestpacSecretConfigured: false
    });

    /* PestPac state - commented out, keeping for potential future use
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [integration, setIntegration] = useState(null);
    const [pestpacCredentials, setPestpacCredentials] = useState({
        clientId: '',
        clientSecret: '',
        tenantId: '',
        apiKey: '',
        username: '',
        password: ''
    });
    const [pestpacUsers, setPestpacUsers] = useState([]);
    const [showUnsyncModal, setShowUnsyncModal] = useState(false);
    const [unsyncConfirmText, setUnsyncConfirmText] = useState('');
    const [usersToUnsync, setUsersToUnsync] = useState([]);
    const [syncSource, setSyncSource] = useState('pestpac');
    */
    
    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            await Promise.allSettled([
                loadPestPacIntegration(),
                loadProviderWebhookConfiguration(),
                loadReviewSolicitationData(true),
                loadFieldRoutesUsers(true)
            ]);
        } catch (error) {
            console.error('Error loading integration:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPestPacIntegration = async () => {
        try {
            const response = await adminAPI.getIntegration('pestpac');
            setPestpacConfigured(Boolean(
                response?.success
                && (response.data?.configured || response.data?.isActive || response.data?.credentials)
            ));
        } catch (error) {
            console.error('Error loading PestPac integration:', error);
            setPestpacConfigured(false);
        }
    };

    const handlePestPacInputChange = (field, value) => {
        setPestpacCredentials((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const loadProviderWebhookConfiguration = async () => {
        try {
            const response = await adminAPI.getProviderWebhookConfiguration();
            if (!response?.success || !response.data) return;
            setProviderWebhooks((previous) => ({
                ...previous,
                pestpacTenantId: response.data.pestpac?.externalAccountId || '',
                pestpacSecretConfigured: Boolean(response.data.pestpac?.secretConfigured)
            }));
        } catch (error) {
            console.error('Error loading provider webhook configuration:', error);
        }
    };

    const handleProviderWebhookInputChange = (field, value) => {
        setProviderWebhooks((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const saveProviderWebhookConfiguration = async () => {
        try {
            setProviderWebhooksSaving(true);
            const response = await adminAPI.saveProviderWebhookConfiguration({
                pestpacWebhookSecret: providerWebhooks.pestpacWebhookSecret
            });
            if (!response?.success) {
                showError(response?.message || 'Failed to save webhook configuration');
                return;
            }

            setProviderWebhooks((previous) => ({
                ...previous,
                pestpacTenantId: response.data?.pestpac?.externalAccountId || '',
                pestpacWebhookSecret: '',
                pestpacSecretConfigured: Boolean(response.data?.pestpac?.secretConfigured)
            }));
            showSuccess('Webhook routing saved for this location');
        } catch (error) {
            console.error('Error saving provider webhook configuration:', error);
            showError(error.message || 'Failed to save webhook configuration');
        } finally {
            setProviderWebhooksSaving(false);
        }
    };

    const savePestPacConfig = async () => {
        const requiredFields = ['clientId', 'clientSecret', 'tenantId', 'apiKey', 'username', 'password'];
        const missingFields = requiredFields.filter((field) => !pestpacCredentials[field].trim());
        if (missingFields.length > 0) {
            showError(`Please fill in all PestPac fields: ${missingFields.join(', ')}`);
            return;
        }

        try {
            setPestpacSaving(true);
            const response = await adminAPI.saveIntegration('pestpac', pestpacCredentials);
            if (!response?.success) {
                showError(response?.message || 'Failed to save PestPac configuration');
                return;
            }

            setPestpacConfigured(true);
            setPestpacCredentials({
                clientId: '',
                clientSecret: '',
                tenantId: '',
                apiKey: '',
                username: '',
                password: ''
            });
            await loadProviderWebhookConfiguration();
            showSuccess('PestPac credentials saved for this location');
        } catch (error) {
            console.error('Error saving PestPac configuration:', error);
            showError(error.message || 'Failed to save PestPac configuration');
        } finally {
            setPestpacSaving(false);
        }
    };

    const testPestPacConnection = async () => {
        try {
            setPestpacTesting(true);
            const response = await adminAPI.testIntegration('pestpac');
            if (response?.success) {
                showSuccess('PestPac connection successful');
            } else {
                showError(response?.message || 'PestPac connection failed');
            }
        } catch (error) {
            console.error('Error testing PestPac connection:', error);
            showError(error.message || 'PestPac connection failed');
        } finally {
            setPestpacTesting(false);
        }
    };

    const loadReviewSolicitationData = async (silent = false) => {
        try {
            setReviewSettingsLoading(true);
            setTestAppointmentsLoading(true);
            const [settingsResponse, statsResponse, testAppointmentsResponse] = await Promise.all([
                adminAPI.getReviewSolicitationSettings(),
                adminAPI.getCampaignStats(),
                adminAPI.getReviewSolicitationTestAppointments({ limit: 50 })
            ]);

            if (settingsResponse?.success && settingsResponse?.data) {
                setReviewSettings(prev => ({
                    ...prev,
                    ...settingsResponse.data
                }));
            }

            if (statsResponse?.success && statsResponse?.data) {
                setCampaignStats({
                    statusBreakdown: statsResponse.data.statusBreakdown || [],
                    responseRate: statsResponse.data.responseRate || '0%'
                });
            }

            if (testAppointmentsResponse?.success && testAppointmentsResponse?.data) {
                const appointments = Array.isArray(testAppointmentsResponse.data.appointments)
                    ? testAppointmentsResponse.data.appointments
                    : [];
                const mockAppointmentId = testAppointmentsResponse.data.mockAppointmentId;

                setTestAppointments(appointments);
                setSelectedTestAppointmentId((previous) => {
                    if (previous && appointments.some((appointment) => String(appointment.id) === String(previous))) {
                        return previous;
                    }

                    if (mockAppointmentId && appointments.some((appointment) => String(appointment.id) === String(mockAppointmentId))) {
                        return String(mockAppointmentId);
                    }

                    return appointments[0] ? String(appointments[0].id) : '';
                });
            }
        } catch (error) {
            console.error('Error loading review solicitation data:', error);
            if (!silent) {
                showError('Failed to load review solicitation settings');
            }
        } finally {
            setReviewSettingsLoading(false);
            setTestAppointmentsLoading(false);
        }
    };

    const handleReviewSettingsChange = (field, value) => {
        setReviewSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const saveReviewSolicitationSettings = async () => {
        try {
            setReviewSettingsSaving(true);

            const response = await adminAPI.updateReviewSolicitationSettings({
                enabled: Boolean(reviewSettings.enabled),
                testModeOnly: true,
                fieldroutesOnly: Boolean(reviewSettings.fieldroutesOnly),
                channel: reviewSettings.channel,
                initialDelayHours: Number(reviewSettings.initialDelayHours || 0),
                platform: reviewSettings.platform || 'google'
            });

            if (response?.success) {
                showSuccess('Review solicitation settings saved');
                await loadReviewSolicitationData(true);
            } else {
                showError(response?.message || 'Failed to save review solicitation settings');
            }
        } catch (error) {
            console.error('Error saving review solicitation settings:', error);
            showError('Failed to save review solicitation settings');
        } finally {
            setReviewSettingsSaving(false);
        }
    };

    const sendTestReviewSolicitation = async () => {
        if (!selectedTestAppointmentId) {
            showError('Select a completed appointment first');
            return;
        }

        try {
            setSendingTestSolicitation(true);
            const response = await adminAPI.sendReviewSolicitationTest({
                appointmentId: Number(selectedTestAppointmentId)
            });

            if (response?.success) {
                showSuccess(`Test solicitation queued (Campaign #${response?.data?.campaignId || 'new'})`);
                await loadReviewSolicitationData(true);
            } else {
                showError(response?.message || 'Failed to send test solicitation');
            }
        } catch (error) {
            console.error('Error sending test review solicitation:', error);
            showError('Failed to send test solicitation');
        } finally {
            setSendingTestSolicitation(false);
        }
    };

    const campaignBreakdown = Array.isArray(campaignStats.statusBreakdown)
        ? campaignStats.statusBreakdown
        : [];
    const totalCampaigns = campaignBreakdown.reduce((sum, item) => sum + Number(item.count || item.dataValues?.count || 0), 0);
    const selectedTestAppointment = testAppointments.find((appointment) => String(appointment.id) === String(selectedTestAppointmentId)) || null;

    /* PestPac functions - commented out, keeping for potential future use
    const loadIntegration = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getIntegration('pestpac');

            if (response.success && response.data) {
                setIntegration({
                    has_pestpac_config: !!(response.data.credentials),
                    users_count: 0,
                    status: 'configured'
                });

                if (response.data.credentials) {
                    setPestpacCredentials({
                        clientId: response.data.credentials.clientId || '',
                        clientSecret: response.data.credentials.clientSecret || '',
                        tenantId: response.data.credentials.tenantId || '',
                        apiKey: response.data.credentials.apiKey || '',
                        username: response.data.credentials.username || '',
                        password: response.data.credentials.password || ''
                    });
                }
            } else {
                setIntegration({
                    has_pestpac_config: false,
                    users_count: 0,
                    status: 'not_configured'
                });
            }
        } catch (error) {
            console.error('Error loading integration:', error);
            setIntegration({
                has_pestpac_config: false,
                users_count: 0,
                status: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setPestpacCredentials(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const testPestPacConnection = async () => {
        try {
            setTesting(true);
            const response = await adminAPI.testIntegration('pestpac');
            if (response.success) {
                showSuccess('PestPac connection successful!');
                return true;
            } else {
                showError(`Connection failed: ${response.message}`);
                return false;
            }
        } catch (error) {
            console.error('Error testing connection:', error);
            showError('Failed to test connection');
            return false;
        } finally {
            setTesting(false);
        }
    };

    const savePestPacConfig = async () => {
        try {
            setSaving(true);
            const requiredFields = ['clientId', 'clientSecret', 'tenantId', 'apiKey', 'username', 'password'];
            const missingFields = requiredFields.filter(field => !pestpacCredentials[field].trim());

            if (missingFields.length > 0) {
                showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
                return;
            }

            const response = await adminAPI.saveIntegration('pestpac', pestpacCredentials);
            if (response.success) {
                showSuccess('PestPac integration configured successfully!');
                await loadIntegration();
            } else {
                showError(`Configuration failed: ${response.message}`);
            }
        } catch (error) {
            console.error('Error saving configuration:', error);
            showError('Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const loadPestPacUsers = async () => {
        try {
            setLoadingUsers(true);
            setPestpacUsers([]);
            setSelectedUsers([]);
            const response = await adminAPI.getPestPacUsers();
            if (response.success) {
                setPestpacUsers(response.data.users);
                showSuccess(`Loaded ${response.data.users.length} PestPac users`);
            } else {
                showError(response.message || 'Failed to load PestPac users');
            }
        } catch (error) {
            console.error('Error loading PestPac users:', error);
            showError('Failed to load PestPac users');
        } finally {
            setLoadingUsers(false);
        }
    };
    */

    const loadFieldRoutesUsers = async (silent = false) => {
        try {
            setLoadingUsers(true);
            setFieldroutesUsers([]);
            setSelectedUsers([]);

            const response = await adminAPI.getFieldRoutesEmployees();

            if (response.success) {
                const users = response.data.users || [];
                setFieldroutesUsers(users);
                setShowSelectiveSync(users.length > 0);
                if (!silent) {
                    showSuccess(`Loaded ${users.length} FieldRoutes employees from webhook data`);
                }
            } else {
                if (!silent) {
                    showError(response.message || 'Failed to load FieldRoutes employees');
                }
            }
        } catch (error) {
            console.error('Error loading FieldRoutes employees:', error);
            if (!silent) {
                showError('Failed to load FieldRoutes employees');
            }
        } finally {
            setLoadingUsers(false);
        }
    };

    const syncSelectedTechnicians = async () => {
        if (selectedUsers.length === 0) {
            showError('Please select at least one user to sync');
            return;
        }

        try {
            setSyncing(true);
            setSyncResult(null);

            const response = await adminAPI.syncFieldRoutesUsers(selectedUsers, syncOptions);

            if (response.success) {
                const totalCreated = response.data.summary?.created || 0;
                const totalUpdated = response.data.summary?.updated || 0;

                setSyncResult(response.data.results);
                showSuccess(`Successfully synced ${totalCreated + totalUpdated} users`);
                setShowSelectiveSync(false);
                setSelectedUsers([]);
                await loadFieldRoutesUsers();
            } else {
                showError(response.message || 'Failed to sync FieldRoutes users');
            }
        } catch (error) {
            console.error('Error syncing users:', error);
            showError('Failed to sync selected users');
        } finally {
            setSyncing(false);
        }
    };

    /* PestPac unsync functions - commented out, keeping for potential future use
    const unsyncSelectedTechnicians = async () => {
        const syncedUsers = selectedUsers.filter(userId => {
            const user = pestpacUsers.find(u => u.id === userId);
            return user && user.isAlreadySynced;
        });

        if (syncedUsers.length === 0) {
            showError('Please select at least one synced user to unsync');
            return;
        }

        setUsersToUnsync(syncedUsers);
        setShowUnsyncModal(true);
        setUnsyncConfirmText('');
    };

    const executeUnsync = async () => {
        if (unsyncConfirmText !== 'DELETE') {
            showWarning('Please type "DELETE" to confirm this permanent action');
            return;
        }

        setShowUnsyncModal(false);

        try {
            setSyncing(true);
            setSyncResult(null);

            const response = await adminAPI.unsyncSelectedUsers(usersToUnsync);

            if (response.success) {
                setSyncResult(response.data.results);
                showSuccess(`Successfully unsynced ${response.data.summary.unsynced} users`);
                setSelectedUsers([]);
                await loadPestPacUsers();
            } else {
                showError(response.message || 'Failed to unsync selected users');
            }
        } catch (error) {
            console.error('Error unsyncing users:', error);
            showError('Failed to unsync selected users');
        } finally {
            setSyncing(false);
        }
    };

    const syncTechnicians = async () => {
        setShowSelectiveSync(true);
        await loadPestPacUsers();
    };
    */
    
    const handleUserSelection = (userId, isSelected) => {
        if (isSelected) {
            setSelectedUsers(prev => [...prev, userId]);
        } else {
            setSelectedUsers(prev => prev.filter(id => id !== userId));
        }
    };

    const handleSelectAll = () => {
        const filteredUsers = getFilteredUsers();
        const allUserIds = filteredUsers.map(user => user.id);
        setSelectedUsers(allUserIds);
    };

    const handleDeselectAll = () => {
        setSelectedUsers([]);
    };

    const getFilteredUsers = () => {
        switch (filterType) {
            case 'technicians':
                return fieldroutesUsers.filter(user => user.isTechnician);
            case 'unsynced':
                return fieldroutesUsers.filter(user => !user.isAlreadySynced);
            case 'synced':
                return fieldroutesUsers.filter(user => user.isAlreadySynced);
            case 'linked':
                return fieldroutesUsers.filter(user => user.hasGhlAccount);
            default:
                return fieldroutesUsers;
        }
    };

    if (loading) {
        return (
            <div className="integration-settings loading">
                <div className="loading-container">
                    <Loader className="spin" size={48} />
                    <h2>Loading Integration Settings...</h2>
                    <p>Setting up your FieldRoutes integration</p>
                </div>
            </div>
        );
    }

    return (
        <div className="integration-settings">
            <div className="integration-container">
                <motion.div
                    className="config-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="header-content">
                        <div className="logo-section">
                            <Zap className="logo" />
                            <div>
                                <h1>Service Integrations</h1>
                                <p>Configure PestPac and sync location-specific FieldRoutes technicians</p>
                            </div>
                        </div>

                        <div className="status-badge">
                            <CheckCircle className="success-icon" />
                            <span>{pestpacConfigured ? 'PestPac Configured' : 'Configuration Required'}</span>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="pestpac-config"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <div className="section-header">
                        <h3><Truck /> PestPac Configuration</h3>
                        <p>
                            Credentials are saved only for the GHL location in this page URL.
                            Stored secrets are never loaded back into the browser.
                        </p>
                    </div>

                    <div className="credentials-form">
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="pestpac-client-id">Client ID</label>
                                <input id="pestpac-client-id" type="text" value={pestpacCredentials.clientId} onChange={(event) => handlePestPacInputChange('clientId', event.target.value)} autoComplete="off" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="pestpac-client-secret">Client Secret</label>
                                <input id="pestpac-client-secret" type="password" value={pestpacCredentials.clientSecret} onChange={(event) => handlePestPacInputChange('clientSecret', event.target.value)} autoComplete="new-password" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="pestpac-tenant-id">Tenant ID</label>
                                <input id="pestpac-tenant-id" type="text" value={pestpacCredentials.tenantId} onChange={(event) => handlePestPacInputChange('tenantId', event.target.value)} autoComplete="off" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="pestpac-api-key">API Key</label>
                                <input id="pestpac-api-key" type="password" value={pestpacCredentials.apiKey} onChange={(event) => handlePestPacInputChange('apiKey', event.target.value)} autoComplete="new-password" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="pestpac-username">Username</label>
                                <input id="pestpac-username" type="email" value={pestpacCredentials.username} onChange={(event) => handlePestPacInputChange('username', event.target.value)} autoComplete="username" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="pestpac-password">Password</label>
                                <input id="pestpac-password" type="password" value={pestpacCredentials.password} onChange={(event) => handlePestPacInputChange('password', event.target.value)} autoComplete="new-password" />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn btn-primary" onClick={savePestPacConfig} disabled={pestpacSaving || pestpacTesting}>
                                {pestpacSaving ? <Loader className="spin" size={16} /> : <CheckCircle size={16} />}
                                Save PestPac Credentials
                            </button>
                            <button type="button" className="btn btn-outline" onClick={testPestPacConnection} disabled={!pestpacConfigured || pestpacSaving || pestpacTesting}>
                                {pestpacTesting ? <Loader className="spin" size={16} /> : <Shield size={16} />}
                                Test Saved Connection
                            </button>
                            <span className={`badge source ${pestpacConfigured ? 'synced' : 'test-mode'}`}>
                                {pestpacConfigured ? 'Configured for this location' : 'Not configured'}
                            </span>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="webhook-config"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                >
                    <div className="section-header">
                        <h3><Shield /> Completed Appointment Webhooks</h3>
                        <p>
                            PestPac uses the saved location credentials and webhook secret.
                            FieldRoutes is received automatically from the existing Ejento listener.
                        </p>
                    </div>

                    <div className="webhook-form">
                        <div className="webhook-provider">
                            <div className="provider-heading">
                                <strong>PestPac</strong>
                                <span className={`badge source ${providerWebhooks.pestpacSecretConfigured ? 'synced' : 'test-mode'}`}>
                                    {providerWebhooks.pestpacSecretConfigured ? 'Webhook secured' : 'Secret required'}
                                </span>
                            </div>
                            <div className="form-group">
                                <label htmlFor="pestpac-webhook-tenant">Tenant ID</label>
                                <input
                                    id="pestpac-webhook-tenant"
                                    type="text"
                                    value={providerWebhooks.pestpacTenantId}
                                    readOnly
                                    placeholder="Save PestPac credentials above first"
                                />
                                <small>Retrieved from the PestPac credentials saved for this location.</small>
                            </div>
                            <div className="form-group">
                                <label htmlFor="pestpac-webhook-secret">Webhook secret</label>
                                <input
                                    id="pestpac-webhook-secret"
                                    type="password"
                                    value={providerWebhooks.pestpacWebhookSecret}
                                    onChange={(event) => handleProviderWebhookInputChange('pestpacWebhookSecret', event.target.value)}
                                    placeholder={providerWebhooks.pestpacSecretConfigured ? 'Leave blank to keep saved secret' : 'Enter the shared PestPac webhook secret'}
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>

                        <div className="webhook-provider">
                            <div className="provider-heading">
                                <strong>FieldRoutes</strong>
                                <span className={`badge source ${fieldroutesUsers.length > 0 ? 'synced' : 'test-mode'}`}>
                                    {fieldroutesUsers.length > 0 ? 'Webhook data detected' : 'Waiting for first event'}
                                </span>
                            </div>
                            <p>
                                No connection or credentials are required here. Completed appointments
                                and technicians are read from the existing location-specific FieldRoutes webhook.
                            </p>
                            <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => loadFieldRoutesUsers(false)}
                                disabled={loadingUsers}
                            >
                                {loadingUsers ? <Loader className="spin" size={16} /> : <Truck size={16} />}
                                Refresh Webhook Data
                            </button>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={saveProviderWebhookConfiguration}
                            disabled={providerWebhooksSaving}
                        >
                            {providerWebhooksSaving ? <Loader className="spin" size={16} /> : <Shield size={16} />}
                            Save Webhook Configuration
                        </button>
                    </div>
                </motion.div>

                <motion.div
                    className="integration-status"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <h3><Shield /> Integration Status</h3>
                    <div className="status-grid">
                        <div className="status-item">
                            <Link className="status-icon success" />
                            <div>
                                <strong>Ejento Elevate</strong>
                                <span>Connected successfully</span>
                            </div>
                        </div>
                        <div className="status-item">
                            <Truck className={`status-icon ${fieldroutesUsers.length > 0 ? 'success' : ''}`} />
                            <div>
                                <strong>FieldRoutes</strong>
                                <span>
                                    {fieldroutesUsers.length > 0
                                        ? `${fieldroutesUsers.length} technicians detected from webhook data`
                                        : 'Waiting for location-specific webhook data'}
                                </span>
                            </div>
                        </div>
                        <div className="status-item">
                            <Users className="status-icon" />
                            <div>
                                <strong>Technicians</strong>
                                <span>{fieldroutesUsers.length > 0 ? 'Loaded automatically' : 'No webhook technicians yet'}</span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="sync-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="section-header">
                        <h3><Users /> Technician Synchronization</h3>
                        <p>FieldRoutes technicians are discovered automatically from stored webhook appointments.</p>
                    </div>

                    {!showSelectiveSync ? (
                        <div className="sync-controls sync-source-buttons">
                            <button
                                className="btn btn-secondary fieldroutes-btn"
                                onClick={() => loadFieldRoutesUsers(false)}
                                disabled={syncing || loadingUsers}
                            >
                                {loadingUsers ? <Loader className="spin" size={18} /> : <Truck size={18} />}
                                Check for Webhook Data
                            </button>
                        </div>
                    ) : (
                        <div className="selective-sync-container">
                            {loadingUsers ? (
                                <div className="loading-users">
                                    <Loader className="spin" size={32} />
                                    <p>Loading FieldRoutes users...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="sync-stats">
                                        <div className="stat">
                                            <strong>{fieldroutesUsers.length}</strong>
                                            <span>Total Users</span>
                                        </div>
                                        <div className="stat fieldroutes">
                                            <strong>{fieldroutesUsers.filter(u => u.isTechnician).length}</strong>
                                            <span>Technicians</span>
                                        </div>
                                        <div className="stat">
                                            <strong>{fieldroutesUsers.filter(u => u.isAlreadySynced).length}</strong>
                                            <span>Synced</span>
                                        </div>
                                        <div className="stat">
                                            <strong>{selectedUsers.length}</strong>
                                            <span>Selected</span>
                                        </div>
                                    </div>

                                    <div className="sync-controls-bar">
                                        <div className="controls-top-row">
                                            <div className="filter-controls">
                                                <label>Filter by:</label>
                                                <select
                                                    value={filterType}
                                                    onChange={(e) => setFilterType(e.target.value)}
                                                    className="filter-select"
                                                >
                                                    <option value="all">All Users</option>
                                                    <option value="technicians">Technicians Only</option>
                                                    <option value="unsynced">Not Synced</option>
                                                    <option value="synced">Already Synced</option>
                                                    <option value="linked">Linked to GHL</option>
                                                </select>
                                            </div>

                                            <div className="selection-controls">
                                                <button
                                                    onClick={handleSelectAll}
                                                    className="btn-sm"
                                                >
                                                    Select All
                                                </button>
                                                <button
                                                    onClick={handleDeselectAll}
                                                    className="btn-sm"
                                                >
                                                    Clear Selection
                                                </button>
                                            </div>
                                        </div>

                                        <div className="sync-actions">
                                            <div className="sync-group">
                                                <button
                                                    onClick={syncSelectedTechnicians}
                                                    disabled={selectedUsers.length === 0 || syncing}
                                                    className="btn-primary"
                                                >
                                                    {syncing ? (
                                                        <>
                                                            <Loader className="spin" size={16} />
                                                            Syncing...
                                                        </>
                                                    ) : (
                                                        `Sync ${selectedUsers.length} Selected`
                                                    )}
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setShowSelectiveSync(false);
                                                    setSelectedUsers([]);
                                                    setFieldroutesUsers([]);
                                                }}
                                                className="btn-outline"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>

                                    <div className="users-list">
                                        {getFilteredUsers().length === 0 ? (
                                            <div className="no-users">No users found for selected filter</div>
                                        ) : (
                                            getFilteredUsers().map(user => (
                                                <div key={user.id} className={`user-item ${user.isAlreadySynced ? 'synced' : ''} fieldroutes`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.includes(user.id)}
                                                        onChange={(e) => handleUserSelection(user.id, e.target.checked)}
                                                    />
                                                    <div className="user-info">
                                                        <span className="user-name">{anonymizeName(user.name)}</span>
                                                        <span className="user-email">{user.email ? anonymizeName(user.email.split('@')[0]) + '@example.com' : 'No email'}</span>
                                                    </div>
                                                    <div className="user-badges">
                                                        <span className="badge source fieldroutes">FieldRoutes</span>
                                                        {user.hasGhlAccount && <span className="badge linked">Linked to GHL</span>}
                                                        {user.isTechnician && <span className="badge tech">Technician</span>}
                                                        {user.isAlreadySynced && <span className="badge synced">Synced</span>}
                                                        <span className="badge id">FR:{user.pestPacId || user.id}</span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {syncResult && (
                        <div className="sync-results">
                            <div className="sync-summary">
                                <h4>Sync Results</h4>
                                <div className="sync-stats">
                                    <span className="stat">
                                        <strong>{syncResult.summary?.total || 0}</strong> <strong>Total Processed</strong>
                                    </span>
                                    <span className="stat success">
                                        <strong>{syncResult.summary?.matched || 0}</strong> <strong>Matched/Created</strong>
                                    </span>
                                    <span className="stat warning">
                                        <strong>{syncResult.summary?.unmatched || 0}</strong> <strong>Unmatched</strong>
                                    </span>
                                    {syncResult.summary?.userMappingsCreated > 0 && (
                                        <span className="stat info">
                                            <strong>{syncResult.summary.userMappingsCreated}</strong> User Mappings Created
                                        </span>
                                    )}
                                </div>
                            </div>

                            {syncResult.matched && syncResult.matched.length > 0 && (
                                <div className="matched-technicians">
                                    <h5>Matched Technicians</h5>
                                    <div className="tech-list">
                                        {syncResult.matched.map((tech, index) => (
                                            <div key={index} className="tech-item matched">
                                                <div className="tech-info">
                                                    <span className="tech-name">{anonymizeName(tech.name)}</span>
                                                    <span className="tech-email">{tech.email ? anonymizeName(tech.email.split('@')[0]) + '@example.com' : ''}</span>
                                                </div>
                                                <div className="tech-badges">
                                                    <span className={`match-badge ${tech.created ? 'created' : 'updated'}`}>
                                                        {tech.created ? 'Created' : `Matched by ${tech.matchedBy}`}
                                                    </span>
                                                    {tech.userMappingCreated && (
                                                        <span className="mapping-badge success">
                                                            User Mapping Created
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {syncResult.unmatched && syncResult.unmatched.length > 0 && (
                                <div className="unmatched-technicians">
                                    <h5>Unmatched Technicians</h5>
                                    <div className="tech-list">
                                        {syncResult.unmatched.map((tech, index) => (
                                            <div key={index} className="tech-item unmatched">
                                                <div className="tech-info">
                                                    <span className="tech-name">{anonymizeName(tech.name)}</span>
                                                    <span className="tech-email">{tech.email ? anonymizeName(tech.email.split('@')[0]) + '@example.com' : ''}</span>
                                                </div>
                                                <span className="unmatch-badge">
                                                    No match found
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {syncResult.errors && syncResult.errors.length > 0 && (
                                <div className="error-technicians">
                                    <h5>Errors</h5>
                                    <div className="tech-list">
                                        {syncResult.errors.map((error, index) => (
                                            <div key={index} className="tech-item error">
                                                <div className="tech-info">
                                                    <span className="tech-name">{anonymizeName(error.name)}</span>
                                                    <span className="tech-email">{error.email ? anonymizeName(error.email.split('@')[0]) + '@example.com' : ''}</span>
                                                </div>
                                                <span className="error-badge">
                                                    {error.error}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>

                <motion.div
                    className="review-solicitation-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                >
                    <div className="section-header">
                        <h3><MessageSquare /> Review Solicitation Messaging</h3>
                        <p>Test-only mode is enabled. Review solicitations are sent only from the manual test panel below.</p>
                    </div>

                    {reviewSettingsLoading ? (
                        <div className="loading-users">
                            <Loader className="spin" size={28} />
                            <p>Loading review solicitation settings...</p>
                        </div>
                    ) : (
                        <div className="review-settings-content">
                            <div className="review-settings-grid">
                                <div className="form-group">
                                    <label>Campaign Status</label>
                                    <div className="toggle-row">
                                        <input
                                            type="checkbox"
                                            id="review-enabled"
                                            checked={Boolean(reviewSettings.enabled)}
                                            onChange={(e) => handleReviewSettingsChange('enabled', e.target.checked)}
                                        />
                                        <label htmlFor="review-enabled">
                                            Enable review solicitation campaign creation
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="review-channel">Message Channel</label>
                                    <select
                                        id="review-channel"
                                        value={reviewSettings.channel}
                                        onChange={(e) => handleReviewSettingsChange('channel', e.target.value)}
                                    >
                                        <option value="sms">SMS</option>
                                        <option value="email">Email</option>
                                        <option value="both">Both</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="review-delay">Initial Delay (hours)</label>
                                    <input
                                        id="review-delay"
                                        type="number"
                                        min="0"
                                        max="168"
                                        value={reviewSettings.initialDelayHours}
                                        onChange={(e) => handleReviewSettingsChange('initialDelayHours', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="review-settings-meta">
                                <span className="badge source test-mode">Mode: Test only</span>
                                <span className="badge source fieldroutes">
                                    Scope: {reviewSettings.fieldroutesOnly ? 'FieldRoutes only' : 'FieldRoutes + PestPac'}
                                </span>
                                <span className="badge synced">Platform: {reviewSettings.platform || 'google'}</span>
                            </div>

                            <div className="review-actions">
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={saveReviewSolicitationSettings}
                                    disabled={reviewSettingsSaving}
                                >
                                    {reviewSettingsSaving ? (
                                        <>
                                            <Loader className="spin" size={16} />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Review Settings'
                                    )}
                                </button>
                            </div>

                            <div className="review-test-panel">
                                <h4>Test Review Solicitation</h4>
                                <p>Select one completed appointment and queue a single test review solicitation.</p>

                                <div className="review-test-controls">
                                    <div className="form-group">
                                        <label htmlFor="review-test-appointment">Completed Appointment</label>
                                        <select
                                            id="review-test-appointment"
                                            value={selectedTestAppointmentId}
                                            onChange={(e) => setSelectedTestAppointmentId(e.target.value)}
                                            disabled={testAppointmentsLoading || sendingTestSolicitation || testAppointments.length === 0}
                                        >
                                            {testAppointments.length === 0 ? (
                                                <option value="">No completed appointments available</option>
                                            ) : (
                                                testAppointments.map((appointment) => {
                                                    const appointmentDate = appointment.serviceDate
                                                        ? new Date(appointment.serviceDate).toLocaleString()
                                                        : 'No date';
                                                    const contactPoint = appointment.customerPhone || appointment.customerEmail || 'No contact';
                                                    const mockLabel = appointment.isMock ? ' [Mock]' : '';

                                                    return (
                                                        <option key={appointment.id} value={appointment.id}>
                                                            {`${appointment.customerName} - ${contactPoint} - ${appointmentDate}${mockLabel}`}
                                                        </option>
                                                    );
                                                })
                                            )}
                                        </select>
                                    </div>

                                    <div className="review-test-actions">
                                        <button
                                            type="button"
                                            className="btn-outline"
                                            onClick={() => loadReviewSolicitationData(false)}
                                            disabled={reviewSettingsLoading || testAppointmentsLoading || sendingTestSolicitation}
                                        >
                                            {testAppointmentsLoading ? (
                                                <>
                                                    <Loader className="spin" size={14} />
                                                    Refreshing...
                                                </>
                                            ) : (
                                                'Refresh Appointments'
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-primary"
                                            onClick={sendTestReviewSolicitation}
                                            disabled={!selectedTestAppointmentId || sendingTestSolicitation || testAppointmentsLoading}
                                        >
                                            {sendingTestSolicitation ? (
                                                <>
                                                    <Loader className="spin" size={14} />
                                                    Sending Test...
                                                </>
                                            ) : (
                                                'Send Test Message'
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {selectedTestAppointment && (
                                    <div className="review-test-summary">
                                        <span>{selectedTestAppointment.customerName}</span>
                                        <span>{selectedTestAppointment.customerPhone || selectedTestAppointment.customerEmail || 'No contact'}</span>
                                        <span>{selectedTestAppointment.serviceType || 'Service'}</span>
                                    </div>
                                )}
                            </div>

                            <div className="review-stats">
                                <div className="stat">
                                    <strong>{totalCampaigns}</strong>
                                    <span>Total Campaigns</span>
                                </div>
                                <div className="stat">
                                    <strong>{campaignStats.responseRate || '0%'}</strong>
                                    <span>Response Rate</span>
                                </div>
                                {campaignBreakdown.slice(0, 4).map((item) => (
                                    <div className="stat" key={item.status || item.dataValues?.status}>
                                        <strong>{item.count || item.dataValues?.count || 0}</strong>
                                        <span>{item.status || item.dataValues?.status}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </motion.div>

                <motion.div
                    className="help-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <h4>Need Help?</h4>
                    <div className="help-content">
                        <p>
                            FieldRoutes integration is connected via webhooks. When appointments are
                            completed in FieldRoutes, they are automatically synced to Ejento Elevate.
                        </p>
                        <p>
                            Use the sync button above to import technicians from FieldRoutes.
                            Technicians will be matched with GoHighLevel users when possible.
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default IntegrationSettings;
